import { PASSAGE_LINKS, PASSAGE_MOUTHS, passagePosition, type PassageMouth } from '@/world/passages';
import type { FloorId } from '@/world/types';
import { dist, clamp } from '@/engine/math';
import { hasLos } from '@/gfx/lighting';
import type { Game } from './game';
import type { Monster } from './entities';
import { findPath } from './nav';
import { boxFree, tileOf, tileCenter } from './physics';
import { stairsToward } from './routing';

type Point = { floor: FloorId; x: number; y: number };
const mouthById = new Map(PASSAGE_MOUTHS.map(p => [p.id, p]));

function floorDistance(g: Game, from: Point, to: Point): number {
  const f = g.mansion.get(from.floor);
  const path = findPath(f, tileOf(from.x), tileOf(from.y), tileOf(to.x), tileOf(to.y), (x,y) => f.doorAt.get(y*f.w+x)?.kind !== 'exit', 8000);
  if (!path) return Infinity;
  let distance = 0, x = from.x, y = from.y;
  for (const [tx, ty] of path) { const nx = tileCenter(tx), ny = tileCenter(ty); distance += dist(x,y,nx,ny); x=nx; y=ny; }
  return distance;
}

function stairDistance(g: Game, from: Point, to: Point, hops = 0): number {
  if (from.floor === to.floor) return floorDistance(g, from, to);
  if (hops > 4) return Infinity;
  const stairs = stairsToward(g.mansion, from.floor, to.floor);
  if (!stairs) return Infinity;
  const arrival = g.mansion.get(stairs.def.toFloor).stairs.find(s => s.def.id === stairs.def.to);
  if (!arrival) return Infinity;
  const approach = floorDistance(g, from, { floor: from.floor, x: tileCenter(stairs.far.x), y: tileCenter(stairs.far.y) });
  return approach + 32 + stairDistance(g, { floor: stairs.def.toFloor, x: tileCenter(arrival.entry.x), y: tileCenter(arrival.entry.y) }, to, hops + 1);
}

export function passageTravelTime(from: string, to: string): number {
  const costs = new Map([[from, 0]]), done = new Set<string>();
  while (done.size < PASSAGE_MOUTHS.length) {
    const next = [...costs].filter(([id]) => !done.has(id)).sort((a,b) => a[1]-b[1])[0];
    if (!next) break;
    const [id, cost] = next;
    if (id === to) return cost;
    done.add(id);
    for (const link of PASSAGE_LINKS) {
      const neighbor = link.a === id ? link.b : link.b === id ? link.a : null;
      if (neighbor && cost + link.seconds < (costs.get(neighbor) ?? Infinity)) costs.set(neighbor, cost + link.seconds);
    }
  }
  return Infinity;
}

/** A shortcut must save real walking distance and have clear, authored entrances. */
export function planPassage(g: Game, m: Monster, goal: Point, speed: number): Monster['passagePlan'] {
  if (m.passageCooldown > 0 || m.state === 'search' || m.state === 'pull') return null;
  const start = { floor: m.floor, x: m.x, y: m.y };
  let bestCost = stairDistance(g, start, goal) * 0.75 - 64;
  let best: Monster['passagePlan'] = null;
  for (const from of PASSAGE_MOUTHS.filter(p => p.floor === m.floor && g.mansion.floors.has(p.floor))) {
    const entry = passagePosition(from);
    if (!boxFree(g.mansion.get(from.floor), entry.x, entry.y, d => d.kind !== 'exit', 6, 4)) continue;
    const approach = floorDistance(g, start, entry);
    if (!Number.isFinite(approach)) continue;
    for (const to of PASSAGE_MOUTHS.filter(p => p.floor === goal.floor && p.id !== from.id)) {
      const exit = passagePosition(to);
      if (!boxFree(g.mansion.get(to.floor), exit.x, exit.y, d => d.kind !== 'exit', 6, 4)) continue;
      const seconds = passageTravelTime(from.id, to.id);
      const cost = approach + (seconds + 2) * speed + floorDistance(g, exit, goal);
      if (cost < bestCost) { bestCost = cost; best = { from: from.id, to: to.id, seconds, goal: { ...goal } }; }
    }
  }
  return best;
}

export function passageEntry(id: string): Point {
  return passagePosition(mouthById.get(id)!);
}

function cue(g: Game, mouth: PassageMouth, arrival: boolean): void {
  const c = g.leader, p = passagePosition(mouth);
  if (!c || c.floor !== p.floor) return;
  const d = dist(c.x, c.y, p.x, p.y);
  if (d > 440) return;
  g.audio.scrape(clamp((p.x - c.x) / 200, -1, 1), Math.max(0.1, 1 - d / 480));
  if (arrival) g.say(`${Math.abs(p.x-c.x) > 64 ? p.x > c.x ? '오른쪽' : '왼쪽' : p.y < c.y ? '위쪽' : '아래쪽'} 벽 안에서 무언가 긁고 있다…`, 2.4);
  if (hasLos(c.x, g.eyeY(c), p.x, p.y - 8, g.occluders(g.floorOf(c)))) {
    g.fx({ kind: 'dust', floor: p.floor, x: p.x, y: p.y - 16, dur: 1.1, r: 12 });
  }
}

export function enterPassage(g: Game, m: Monster): void {
  const plan = m.passagePlan!;
  m.transit = { from: plan.from, to: plan.to, seconds: plan.seconds, phase: 'enter', t: 0.8, held: 0, warned: false };
  m.passagePlan = null; m.path = null;
  m.facing = 'up';
  cue(g, mouthById.get(plan.from)!, false);
}

function clearExit(g: Game, m: Monster, p: Point): boolean {
  return boxFree(g.mansion.get(p.floor), p.x, p.y, d => d.kind !== 'exit', 6, 4) &&
    [...g.chars.values()].every(c => c.floor !== p.floor || !['free','groggy','hidden'].includes(c.state) || m.carrying.includes(c.id) || dist(c.x,c.y,p.x,p.y) >= 96);
}

/** Returns true while the creature is committed to the cavity, before it can attack again. */
export function updatePassage(g: Game, m: Monster, dt: number): boolean {
  const trip = m.transit;
  if (!trip) return false;
  trip.t -= dt;
  if (trip.phase === 'enter' && trip.t <= 0) { trip.phase = 'cross'; trip.t = trip.seconds; }
  if (trip.phase === 'cross') {
    if (trip.t <= 2.8 && !trip.warned) { cue(g, mouthById.get(trip.to)!, true); trip.warned = true; }
    if (trip.t <= 1.2) {
      let exit = passageEntry(trip.to);
      if (!clearExit(g, m, exit)) {
        trip.t = 1.2; trip.held += dt;
        // A camper never gets a creature spawned on top of them; it backs out instead.
        const origin = passageEntry(trip.from);
        if (trip.held < 6 || !clearExit(g, m, origin)) return true;
        exit = origin;
        cue(g, mouthById.get(trip.from)!, true);
        trip.to = trip.from;
      }
      m.floor = exit.floor; m.x = exit.x; m.y = exit.y; m.facing = 'down';
      trip.phase = 'emerge'; trip.t = 1.2;
    }
  } else if (trip.phase === 'emerge' && trip.t <= 0) {
    m.transit = null; m.passageCooldown = 35; m.path = null; m.pathTimer = 0;
  }
  return true;
}

export function passageOpacity(m: Monster): number {
  if (!m.transit) return 1;
  return m.transit.phase === 'enter' ? clamp(m.transit.t / 0.8, 0, 1) : m.transit.phase === 'cross' ? 0 : clamp(1 - m.transit.t / 1.2, 0, 1);
}
